import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { BsCurrencyExchange } from "react-icons/bs";
import { AiOutlineCaretDown, AiOutlineCaretUp } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { updateOrderFinancials } from "../../../../services/fhpService";
import { showSuccessToast } from "../../../../services/swalService";
import { convertPrice, formatAgentWalletAmountIls } from "../../../../services/currencyService";

import "./HandlingFeePicker.scss";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { isVacationPackagesFlightsHotelAgentDeal } from "../../../../utils/vacationPackageRoomAgentMarkup.js";
import {
  handlingFeeDraftFromStoredValue,
  isFixedPlusAmountAuthoritativeIls,
  resolveOrderFinancialsHandlingFee,
  shouldDeferHandlingFeeDraftSync,
} from "./handlingFeeAmount.js";

export const HPM_COMMISSION_CHIPS_SLOT_ID = "hpm-live-commission-chips";

/** Debounce before blur/commit pushes fee into SelectedResultContext (rooms_matrix + LDS). */
const FEE_CONTEXT_COMMIT_DEBOUNCE_MS = 320;

const quickAddCommisionAdd = [
  { value: 400, type: "+" },
  { value: 600, type: "+" },
  { value: 800, type: "+" },
  { value: 1000, type: "+" },
];
const quickAddCommisionPercentage = [
  { value: 5, type: "%" },
  { value: 10, type: "%" },
  { value: 12, type: "%" },
  { value: 15, type: "%" },
];

function normalizeFeeValue(rawVal) {
  const n = Number(rawVal);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Keep draft as string while typing so "" / intermediate digits never lift to context. */
function feeValueToDraftString(raw) {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? String(n) : "";
}

const HandlingFeePicker = ({
  updateHandleFeeData,
  handleFeeData,
  extendHandleFeeDefault = false,
  orderId = null,
  orderCommission = 0,
  ourPrice = null,
  createCustomerPriceQuote = null,
  className = "",
  fixedFeeStoredCurrency = "USD",
  /**
   * Organized tours store "+" in ILS on the order. Other ILS pickers still
   * send updateOrderFinancials through the legacy USD conversion.
   */
  persistFixedPlusAsIls = false,
  /**
   * When true: commit on blur/type flip without waiting for «עדכן».
   * NEVER commit on each keystroke — that freezes package profile (rooms_matrix + LDS).
   */
  applyHandleFeeToPriceImmediately = false,
  /**
   * "hpm" = packageProfileMockup agent commission chrome (hpm-agent-compact__* / chips).
   * Default keeps legacy payment-agent accordion UI.
   */
  layoutVariant = null,
  /** When layoutVariant=hpm — show quick chips (parent may gate via "אפשרויות נוספות") */
  showQuickChips = true,
  /** Portal chips into this element id (mock "more" panel). Falls back to inline. */
  chipsPortalId = null,
  /** When set, the success toast shows this customer price instead of the card-clearing total. */
  customerPriceIls = null,
}) => {
  const isHpm = layoutVariant === "hpm";
  const [extendHandleFee, setExtendHandleFee] = useState(
    isHpm ? true : extendHandleFeeDefault
  );
  const [chipsPortalEl, setChipsPortalEl] = useState(null);
  const [draftFeeText, setDraftFeeText] = useState(() =>
    feeValueToDraftString(handleFeeData?.value ?? 0)
  );
  const [editingHandleFeeOption, setEditingHandleFeeOption] = useState(
    handleFeeData?.type || "+"
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const previousPriceRef = useRef(null);
  const hasUpdatedRef = useRef(false);
  const isSavingRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const pendingSaveSnapshotRef = useRef(null);
  const commitTimeoutRef = useRef(null);
  const draftFeeTextRef = useRef(draftFeeText);
  const editingOptionRef = useRef(editingHandleFeeOption);
  draftFeeTextRef.current = draftFeeText;
  editingOptionRef.current = editingHandleFeeOption;

  const { t } = useTranslation();

  const { ilsToUsd, usdToIls, selectedResult } = useSelectedResult();

  const vacationPackageFixedCommissionAuthoritativeIls =
    isVacationPackagesFlightsHotelAgentDeal(selectedResult);
  const fixedPlusAmountIsAuthoritativeIls = isFixedPlusAmountAuthoritativeIls({
    fixedFeeStoredCurrency,
    vacationPackageDeal: vacationPackageFixedCommissionAuthoritativeIls,
  });

  const feeEqualsContext = useCallback(
    (opt, rawVal) => {
      const n = normalizeFeeValue(rawVal);
      return (
        String(handleFeeData?.type || "") === String(opt || "") &&
        Number(handleFeeData?.value) === n
      );
    },
    [handleFeeData?.type, handleFeeData?.value]
  );

  /**
   * Push fee into SelectedResultContext.
   * Equality-guarded. Never call from input onChange — only Apply / debounced blur / chips.
   * `force` is required when applyHandleFeeToPriceImmediately is false (HPM / package island).
   */
  const pushHandleToContext = useCallback(
    (opt, rawVal, { force = false } = {}) => {
      if (typeof updateHandleFeeData !== "function") return false;
      if (!force && !applyHandleFeeToPriceImmediately) return false;
      if (feeEqualsContext(opt, rawVal)) return false;
      updateHandleFeeData(opt, normalizeFeeValue(rawVal));
      return true;
    },
    [updateHandleFeeData, applyHandleFeeToPriceImmediately, feeEqualsContext]
  );

  const clearCommitTimer = () => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
  };

  /** Debounced context commit — keeps typing / blur from storming rooms_matrix. */
  const scheduleContextCommit = useCallback(
    (opt, rawVal, { force = true, debounceMs = FEE_CONTEXT_COMMIT_DEBOUNCE_MS } = {}) => {
      clearCommitTimer();
      const nextOpt = opt || "+";
      const nextVal = normalizeFeeValue(rawVal);
      if (feeEqualsContext(nextOpt, nextVal)) return;
      commitTimeoutRef.current = setTimeout(() => {
        commitTimeoutRef.current = null;
        pushHandleToContext(nextOpt, nextVal, { force });
      }, debounceMs);
    },
    [feeEqualsContext, pushHandleToContext]
  );

  const readCommissionInput = () => {
    const el = document.getElementById("commission");
    if (el && el.value !== undefined && el.value !== null) {
      return normalizeFeeValue(el.value);
    }
    return normalizeFeeValue(draftFeeTextRef.current);
  };

  const chipSelected = (item) =>
    normalizeFeeValue(draftFeeText) === item.value &&
    editingHandleFeeOption === item.type;

  const openCloseHandleFee = () => {
    setExtendHandleFee(!extendHandleFee);
  };

  const saveCommission = async (commissionSum) => {
    if (isSavingRef.current) {
      return;
    }

    // Prefer controlled draft ref — never trust a stale/foreign #commission node.
    let updatedCommission =
      commissionSum != null && commissionSum !== ""
        ? normalizeFeeValue(commissionSum)
        : normalizeFeeValue(draftFeeTextRef.current);

    if (parseFloat(updatedCommission) >= 0) {
      hasUpdatedRef.current = true;
      previousPriceRef.current =
        ourPrice?.priceWithCreditCardFee ||
        ourPrice?.priceWithHandleFee ||
        ourPrice?.priceWithCommission ||
        0;

      const feeMeta =
        editingOptionRef.current === "+" &&
        Number.isFinite(Number(updatedCommission)) &&
        Number(updatedCommission) >= 0
          ? { fixedCommissionInputIls: Math.round(Number(updatedCommission)) }
          : {};
      // Always push on עדכן, once. Skip when this exact fee is already applied.
      if (!feeEqualsContext(editingOptionRef.current, updatedCommission)) {
        updateHandleFeeData(editingOptionRef.current, updatedCommission, feeMeta);
      }

      if (orderId && orderId !== "newOrder") {
        isSavingRef.current = true;
        setIsUpdating(true);
        try {
          await new Promise((resolve) => setTimeout(resolve, 500));

          const pwc = Number(
            ourPrice?.priceWithCommission ??
              ourPrice?.ourTotalFare?.priceWithCommission ??
              0
          );
          let absoluteIls = Number(updatedCommission);
          if (editingOptionRef.current === "%") {
            absoluteIls =
              Number.isFinite(pwc) && pwc > 0
                ? Math.round(((pwc * Number(updatedCommission)) / 100) * 100) /
                  100
                : Number(ourPrice?.handleFee) || 0;
          }

          if (!vacationPackageFixedCommissionAuthoritativeIls) {
            const commissionForBackend = resolveOrderFinancialsHandlingFee({
              type: editingOptionRef.current || "+",
              absoluteIls,
              persistFixedPlusAsIls,
              ilsToUsd,
            });
            await Promise.race([
              updateOrderFinancials({
                orderID: orderId,
                handlingfee: commissionForBackend,
                handlingFeeOption:
                  editingOptionRef.current === "%"
                    ? "+"
                    : editingOptionRef.current,
              }),
              new Promise((_, reject) =>
                setTimeout(
                  () => reject(new Error("Update timeout after 10 seconds")),
                  10000
                )
              ),
            ]);
          }

          if (createCustomerPriceQuote) {
            try {
              await Promise.race([
                createCustomerPriceQuote({
                  moveNextStateFlag: false,
                  later: false,
                }),
                new Promise((_, reject) =>
                  setTimeout(
                    () => reject(new Error("Save timeout after 30 seconds")),
                    30000
                  )
                ),
              ]);
            } catch (err) {
              if (err.message && err.message.includes("timeout")) {
                message.warning(
                  "העמלה עודכנה, אך עדכון מלא של ההזמנה ארך זמן רב. המערכת תנסה שוב ברקע."
                );
              } else {
                message.error(
                  "שגיאה בעדכון ההזמנה המלא. העמלה עודכנה בהצלחה."
                );
              }
            }
          }
        } catch (error) {
          const errorMessage =
            error.message && error.message.includes("timeout")
              ? "עדכון העמלה ארך זמן רב מדי. אנא נסה שוב."
              : t("errorMessages.updateFailed") ||
                "Failed to update handling fee";
          message.error(errorMessage);
        } finally {
          setIsUpdating(false);
          setTimeout(() => {
            isSavingRef.current = false;
          }, 1000);
        }
      } else {
        isSavingRef.current = false;
      }
    }
  };

  // Sync draft FROM context only when context actually changes (not while typing).
  useEffect(() => {
    if (
      shouldDeferHandlingFeeDraftSync(
        saveTimeoutRef.current || pendingSaveSnapshotRef.current
      )
    ) {
      return;
    }
    const type = handleFeeData?.type;
    const raw = handleFeeData?.value;
    if (type && raw !== undefined && raw !== null) {
      const next = handlingFeeDraftFromStoredValue(raw, type, {
        authoritativeIls: fixedPlusAmountIsAuthoritativeIls,
        usdToIls,
      });
      const nextStr = feeValueToDraftString(next);
      setDraftFeeText((prev) => (prev === nextStr ? prev : nextStr));
      setEditingHandleFeeOption((prev) => (prev === type ? prev : type));
      return;
    }
    setEditingHandleFeeOption((prev) => (prev === "+" ? prev : "+"));
    setDraftFeeText((prev) => (prev === "0" || prev === "" ? prev : "0"));
  }, [
    handleFeeData?.type,
    handleFeeData?.value,
    usdToIls,
    fixedPlusAmountIsAuthoritativeIls,
  ]);

  useEffect(() => {
    if (hasUpdatedRef.current && ourPrice && handleFeeData) {
      const quotedCustomer = Number(customerPriceIls);
      const currentPrice =
        Number.isFinite(quotedCustomer) && quotedCustomer > 0
          ? quotedCustomer
          : ourPrice?.priceWithCreditCardFee ||
            ourPrice?.priceWithHandleFee ||
            ourPrice?.priceWithCommission ||
            0;

      const timeoutId = setTimeout(() => {
        if (currentPrice !== previousPriceRef.current && currentPrice > 0) {
          const priceStr = fixedPlusAmountIsAuthoritativeIls
            ? formatAgentWalletAmountIls(currentPrice)
            : convertPrice(currentPrice);
          let commissionValue = "";
          if (handleFeeData.type === "%") {
            commissionValue = `${handleFeeData.value}%`;
          } else {
            commissionValue = fixedPlusAmountIsAuthoritativeIls
              ? formatAgentWalletAmountIls(handleFeeData.value)
              : convertPrice(handleFeeData.value);
          }

          showSuccessToast(
            `עמלה הוספה בהצלחה! ${commissionValue}\nמחיר ללקוח: ${priceStr}`
          );
          hasUpdatedRef.current = false;
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    ourPrice?.priceWithCreditCardFee,
    ourPrice?.priceWithHandleFee,
    ourPrice?.priceWithCommission,
    handleFeeData,
    vacationPackageFixedCommissionAuthoritativeIls,
    fixedPlusAmountIsAuthoritativeIls,
    customerPriceIls,
  ]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      pendingSaveSnapshotRef.current = null;
      clearCommitTimer();
    };
  }, []);

  useEffect(() => {
    if (
      !isHpm ||
      !chipsPortalId ||
      !showQuickChips ||
      typeof document === "undefined"
    ) {
      setChipsPortalEl((prev) => (prev == null ? prev : null));
      return undefined;
    }
    const sync = () => {
      const el = document.getElementById(chipsPortalId);
      setChipsPortalEl((prev) => (prev === el ? prev : el));
    };
    sync();
    const t = window.setTimeout(sync, 0);
    return () => window.clearTimeout(t);
  }, [isHpm, chipsPortalId, showQuickChips]);

  const triggerSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    // Freeze the typed amount before the context→draft effect can rewrite it.
    pendingSaveSnapshotRef.current = {
      opt: editingOptionRef.current || "+",
      raw: normalizeFeeValue(draftFeeTextRef.current),
    };
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      const snap = pendingSaveSnapshotRef.current;
      pendingSaveSnapshotRef.current = null;
      if (snap) {
        editingOptionRef.current = snap.opt;
        setEditingHandleFeeOption(snap.opt);
        saveCommission(snap.raw);
        return;
      }
      saveCommission();
    }, 300);
  };

  const applyChip = (item) => {
    clearCommitTimer();
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingSaveSnapshotRef.current = null;
    const next = String(item.value);
    editingOptionRef.current = item.type;
    draftFeeTextRef.current = next;
    setEditingHandleFeeOption(item.type);
    setDraftFeeText(next);
    const el = document.getElementById("commission");
    if (el) el.value = next;
  };

  const applyDraftOnce = () => {
    clearCommitTimer();
    triggerSave();
  };

  const onDraftInputChange = (e) => {
    // LOCAL ONLY — never updateHandleFeeData / LDS / rooms_matrix on keystroke.
    const raw = e.target.value;
    if (raw === "") {
      setDraftFeeText("");
      return;
    }
    // Allow digits only in the draft; reject negatives / NaN without lifting context.
    if (!/^\d*\.?\d*$/.test(raw)) return;
    setDraftFeeText(raw);
  };

  const onDraftBlur = () => {
    // HPM / deferred: debounce ≥300ms + equality guard.
    // Immediate mode (organized tours): still debounced to avoid focus thrash storms.
    scheduleContextCommit(
      editingOptionRef.current || "+",
      draftFeeTextRef.current,
      {
        force: true,
        debounceMs: FEE_CONTEXT_COMMIT_DEBOUNCE_MS,
      }
    );
  };

  const onTypeChange = (opt) => {
    // LOCAL ONLY — %/+ flip must not push context (was a rooms_matrix storm source).
    // Context updates on blur / עדכן / chips only.
    setEditingHandleFeeOption(opt);
  };

  if (isHpm) {
    const appliedValue = Number(handleFeeData?.value);
    const hasApplied = Number.isFinite(appliedValue) && appliedValue > 0;
    return (
      <div className={classNames("hpm-handling-fee", className)}>
        <label className="hpm-agent-compact__field">
          <span className="hpm-agent-compact__label">
            <BsCurrencyExchange aria-hidden /> עמלה נוספת
          </span>
          <div className="hpm-agent-compact__row">
            <select
              value={editingHandleFeeOption || "+"}
              aria-label="סוג עמלה"
              id="commission-options"
              dir="ltr"
              disabled={isUpdating || isSavingRef.current}
              onChange={(e) => onTypeChange(e.target.value)}
            >
              <option value="+">+</option>
              <option value="%">%</option>
            </select>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              dir="ltr"
              id="commission"
              value={draftFeeText}
              aria-label="סכום עמלה נוספת"
              disabled={isUpdating || isSavingRef.current}
              onChange={onDraftInputChange}
              onBlur={onDraftBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyDraftOnce();
                }
              }}
            />
            <button
              type="button"
              className="hpm-agent-compact__update"
              disabled={isUpdating || isSavingRef.current}
              onClick={applyDraftOnce}
            >
              {isUpdating ? "מעדכן…" : t("paymentagent.update") || "עדכן"}
            </button>
          </div>
        </label>
        {hasApplied ? (
          <p className="hpm-agent-compact__applied" aria-live="polite">
            בסיכום:{" "}
            {handleFeeData?.type === "%"
              ? `${handleFeeData.value}%`
              : fixedPlusAmountIsAuthoritativeIls
                ? formatAgentWalletAmountIls(handleFeeData.value)
                : convertPrice(handleFeeData.value)}
          </p>
        ) : null}
        {(() => {
          if (!showQuickChips) return null;
          const chips = (
            <>
              <div className="hpm-commission__chips-block">
                <span className="hpm-commission__chips-label">סכומים</span>
                <div className="hpm-commission__chips">
                  {quickAddCommisionAdd.map((item) => (
                    <button
                      key={`${item.type}-${item.value}`}
                      type="button"
                      className={`hpm-commission__chip${
                        chipSelected(item) ? " is-selected" : ""
                      }`}
                      disabled={isUpdating || isSavingRef.current}
                      onClick={() => applyChip(item)}
                    >
                      +{item.value}
                    </button>
                  ))}
                </div>
              </div>
              <div className="hpm-commission__chips-block">
                <span className="hpm-commission__chips-label">אחוזים</span>
                <div className="hpm-commission__chips">
                  {quickAddCommisionPercentage.map((item) => (
                    <button
                      key={`${item.type}-${item.value}`}
                      type="button"
                      className={`hpm-commission__chip${
                        chipSelected(item) ? " is-selected" : ""
                      }`}
                      disabled={isUpdating || isSavingRef.current}
                      onClick={() => applyChip(item)}
                    >
                      {item.value}%
                    </button>
                  ))}
                </div>
              </div>
            </>
          );
          if (chipsPortalEl) return createPortal(chips, chipsPortalEl);
          return chips;
        })()}
      </div>
    );
  }

  return (
    <div className={classNames("payment-order-commition-wrapper", className)}>
      <div className="payment-commission-wrapper" onClick={openCloseHandleFee}>
        <div className="payment-commission-main-item">
          <span>{t("paymentagent.addcommission")}</span>
          <BsCurrencyExchange />
        </div>

        {!extendHandleFee ? <AiOutlineCaretDown /> : <AiOutlineCaretUp />}
      </div>

      {extendHandleFee && (
        <div className="payment-commission-drop-wrapper-wrapper">
          <div className="payment-commission-drop-wrapper">
            <div className="payment-commission-drop-title-wrapper">
              <select
                value={editingHandleFeeOption || "+"}
                className="commission-options"
                name="commission-options"
                id="commission-options"
                onChange={(e) => {
                  const opt = e.target.value;
                  setEditingHandleFeeOption(opt);
                  if (applyHandleFeeToPriceImmediately) {
                    scheduleContextCommit(opt, readCommissionInput(), {
                      force: false,
                      debounceMs: FEE_CONTEXT_COMMIT_DEBOUNCE_MS,
                    });
                  }
                }}
              >
                <option value="+">+</option>
                <option value="%">%</option>
              </select>
              <span className="payment-commission-drop-title">
                {t("paymentagent.addedcommission")}
              </span>

              <BsCurrencyExchange className="payment-commission-drop-title-icon" />
            </div>

            <div className="payment-commission-drop-update-wrapper">
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={draftFeeText}
                className="payment-commission-drop-update-input"
                id="commission"
                onChange={onDraftInputChange}
                onBlur={() => {
                  if (applyHandleFeeToPriceImmediately) {
                    scheduleContextCommit(
                      editingOptionRef.current || "+",
                      draftFeeTextRef.current,
                      { force: false, debounceMs: FEE_CONTEXT_COMMIT_DEBOUNCE_MS }
                    );
                  } else {
                    // Package profile / deferred: still commit on blur (debounced).
                    scheduleContextCommit(
                      editingOptionRef.current || "+",
                      draftFeeTextRef.current,
                      { force: true, debounceMs: FEE_CONTEXT_COMMIT_DEBOUNCE_MS }
                    );
                  }
                }}
              />
            </div>
          </div>
          <div className="quick-add-commision-btns quick-add-commision-btns--labeled">
            <span className="quick-add-commision-label" aria-hidden>
              סכומים
            </span>
            <div className="quick-add-commision-chips quick-add-commision-chips--amount">
              {quickAddCommisionAdd.map((item) => (
                <button
                  key={`${item.type}-${item.value}`}
                  type="button"
                  className={classNames("commission-chip", {
                    "commission-chip--selected": chipSelected(item),
                  })}
                  onClick={() => applyChip(item)}
                >
                  +{item.value}
                </button>
              ))}
            </div>
          </div>
          <div className="quick-add-commision-btns quick-add-commision-btns--labeled">
            <span className="quick-add-commision-label" aria-hidden>
              אחוזים
            </span>
            <div className="quick-add-commision-chips">
              {quickAddCommisionPercentage.map((item) => (
                <button
                  key={`${item.type}-${item.value}`}
                  type="button"
                  className={classNames("commission-chip", {
                    "commission-chip--selected": chipSelected(item),
                  })}
                  onClick={() => applyChip(item)}
                >
                  {item.value}%
                </button>
              ))}
            </div>
          </div>
          <button
            className="payment-commission-drop-update-btn"
            onClick={applyDraftOnce}
            disabled={isUpdating || isSavingRef.current}
            style={{
              opacity: isUpdating || isSavingRef.current ? 0.6 : 1,
              cursor:
                isUpdating || isSavingRef.current ? "not-allowed" : "pointer",
            }}
          >
            {isUpdating ? (
              <>
                <span className="tw-inline-block tw-animate-spin tw-mr-2">
                  ⏳
                </span>
                {t("paymentagent.updating") || "Updating..."}
              </>
            ) : (
              t("paymentagent.update")
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(HandlingFeePicker);
